import { configureStore, combineReducers } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { TypedUseSelectorHook, useSelector as rawUseSelector } from 'react-redux';
import reducers from './reducers';
import { NAMESPACE as SOCIAL_NAMESPACE } from '../social/slice';
import { NAMESPACE as PLAN_CONFIG_NAMESPACE } from '../planConfig/slice';
import { NAMESPACE as BOOKING_NAMESPACE } from '../booking/slice';
import { NAMESPACE as CANCELLATION_NAMESPACE } from '../cancellation/slice';

declare module 'react-redux' {
  export type DefaultRootState = RootState;
}

const rootReducer = combineReducers({
  ...reducers,
});

/**
 * NOTE: If you change any of the whitelisted states,
 * Please increment this by one
 */
const CURRENT_VERSION = 5;

const persistedReducer = persistReducer(
  {
    key: 'root',
    storage,
    whitelist: [PLAN_CONFIG_NAMESPACE, BOOKING_NAMESPACE, CANCELLATION_NAMESPACE, SOCIAL_NAMESPACE],
    version: CURRENT_VERSION,
    migrate: (state, currentVersion) => {
      /**
       * NOTE: if the state in LocalStorage is the same as current version
       * we keep the state and move on.
       * Otherwise we throw it away.
       *
       * tl;dr
       * We don't do migration, for now.
       * Future developer; please add migration if needed.
       */
      if (currentVersion === CURRENT_VERSION) {
        return Promise.resolve(state);
      }
      return Promise.resolve(undefined);
    },
  },
  rootReducer
);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export type RootState = ReturnType<typeof store.getState>;

const useSelector: TypedUseSelectorHook<RootState> = rawUseSelector;

// IMPORTANT: persistStore() rehydrates the store in the client side.
if (typeof window !== 'undefined') persistStore(store);

export { store, useSelector };
